import { GridOptions, ProcessCellForExportParams } from '@ag-grid-community/core';

import { PatientProtocolSubType } from '@shared/services/gql.service';
import { agParseValueFromClipboard } from '@shared/utils/ag-copy-paste.utils';

import { getProtocolEntryColumns } from './protocol-entry-columns';
import { PROTOCOL_ENTRY_DEFAULT_CONFIG } from './protocol-entry-default-config';
import { PATIENT_PROTOCOL_VALIDATION_MESSAGE } from '../../../constants/patient-protocol-validation-messages.const';
import { ProtocolEntryGridContext } from '../types/protocol-entry-grid-context';
import { ProtocolEntryGridRow } from '../types/protocol-entry-grid-row';

export const protocolEntryConfig = (
  cxt: ProtocolEntryGridContext
): GridOptions<ProtocolEntryGridRow> => {
  const config = PROTOCOL_ENTRY_DEFAULT_CONFIG;
  const columnDefs = getProtocolEntryColumns(cxt);

  return {
    ...config,
    columnDefs,
    defaultColDef: {
      ...config.defaultColDef,
      editable: cxt.editMode,
    },
    processCellForClipboard: (params: ProcessCellForExportParams) => {
      return subTypeCopyForClipboard(params);
    },
    processCellFromClipboard: (params: ProcessCellForExportParams) => {
      return subTypeCopyFromClipboard(params, cxt);
    },
  };
};

function subTypeCopyForClipboard(params: ProcessCellForExportParams): number | string {
  // Return default
  if (typeof params.value !== 'object') {
    return params.value;
  }

  // Return default if object isn't PatientProtocolSubType
  if (!('patient_protocol_type' in params.value)) {
    return params.value;
  }

  // Prefix to find copied PatientProtocolSubType
  // objects on paste
  const prefix = 'SUBTYPE_COPY_';
  const subType = JSON.stringify(params.value);

  return prefix + subType;
}

// Process paste value from clipboard (to grid)
function subTypeCopyFromClipboard(
  params: ProcessCellForExportParams,
  cxt: ProtocolEntryGridContext
) {
  return agParseValueFromClipboard<{
    patient_protocol_frequency: string;
    patient_protocol_name: string;
    patient_protocol_sub_type: Record<string, unknown>;
    target_date_days_out: string;
    target_tolerance_days_out: string;
  }>(
    params,
    new Map([
      ['patient_protocol_name', { type: 'alphanumeric' }],
      [
        'patient_protocol_sub_type',
        {
          parseFn: () => {
            const prefix = 'SUBTYPE_COPY_';

            if (params.value.includes(prefix)) {
              const subTypeString = params.value.split(prefix).pop();

              return JSON.parse(subTypeString) as PatientProtocolSubType;
            }

            cxt.overlayService.error(PATIENT_PROTOCOL_VALIDATION_MESSAGE.TYPE);

            return params.node?.data?.patient_protocol_sub_type;
          },
          type: 'custom',
        },
      ],
      ['target_date_days_out', { type: 'numeric' }],
      ['target_tolerance_days_out', { type: 'numeric' }],
    ])
  );
}
